import { FileText, Globe, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import { useState } from 'react';
import type { SourceInfo } from '@/types';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface GroundingSourcesProps {
  sources: SourceInfo[];
}

export function GroundingSources({ sources }: GroundingSourcesProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (sources.length === 0) return null;

  const displayedSources = isExpanded ? sources : sources.slice(0, 3);
  const hasMore = sources.length > 3;

  return (
    <div className="mt-4 border-t pt-3">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-sm font-medium text-foreground">Grounding Sources</span>
        <Badge variant="outline" className="text-[10px] px-2 py-0">{sources.length}</Badge>
      </div>
      
      <TooltipProvider>
      <div className="space-y-2">
        {displayedSources.map((source, idx) => (
          <SourceItem key={idx} source={source} index={idx + 1} />
        ))}
      </div>
      </TooltipProvider>

      {hasMore && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsExpanded(!isExpanded)}
          className="mt-2 text-xs"
        >
          {isExpanded ? (
            <>
              <ChevronUp className="w-3 h-3 mr-1" />
              Show less
            </>
          ) : (
            <>
              <ChevronDown className="w-3 h-3 mr-1" />
              Show {sources.length - 3} more
            </>
          )}
        </Button>
      )}
    </div>
  );
}

interface SourceItemProps {
  source: SourceInfo;
  index: number;
}

function SourceItem({ source, index }: SourceItemProps) {
  const isWebSource = source.url.startsWith('http');
  // const isGcsSource = source.url.startsWith('gs://');

  const getDisplayName = (title: string, url: string) => {
    const generic = !title || title.toLowerCase() === 'document' || title.toLowerCase() === 'source' || title.toLowerCase() === 'web source';
    if (!generic) {
      return title.replace(/\.json$/i, '.pdf');
    }
    try {
      const cleanUrl = url.split('?')[0].split('#')[0];
      const parts = cleanUrl.split('/');
      const name = decodeURIComponent(parts[parts.length - 1] || 'Document');
      return name.replace(/\.json$/i, '.pdf');
    } catch {
      return (title || 'Document').replace(/\.json$/i, '.pdf');
    }
  };

  const name = getDisplayName(source.title, source.url);
  const ext = name.includes('.') ? name.split('.').pop()?.toUpperCase() : undefined;

  return (
    <div className="flex items-center gap-3 p-2 rounded-lg border bg-card/40 hover:bg-accent/20 transition-colors">
      <div className="flex-shrink-0 mt-0.5">
        {isWebSource ? (
          <Globe className="w-4 h-4 text-blue-500" />
        ) : (
          <FileText className="w-4 h-4 text-muted-foreground" />
        )}
      </div>
      
      <div className="flex-1 min-w-0 flex items-center gap-2">
        <Badge variant="secondary" className="text-[10px] px-2 py-0">{index}</Badge>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="text-sm font-medium truncate max-w-[22rem]">
              {name}
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" align="start" className="text-xs">
            {name}
          </TooltipContent>
        </Tooltip>
        {!isWebSource && ext && (
          <Badge variant="outline" className="ml-auto text-[10px] px-2 py-0">
            {ext}
          </Badge>
        )}
        {isWebSource && source.permalink && (
          <a
            href={source.permalink}
            target="_blank"
            rel="noopener noreferrer"
            className="ml-auto inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
          >
            <ExternalLink className="w-3 h-3" />
            Permalink
          </a>
        )}
      </div>
    </div>
  );
}
